import {
  useBackend,
  useBundlesPrices,
  useCookieLensAdvisor,
  useEnableRemoveLenses,
  useEnableSpeedyRX,
  useFlowName,
  useGetReviewObjectLensAdvisor,
  usePreCheck,
  useShowFramePlusLensesPrice,
} from '@hooks';
import { useQuery } from '@tanstack/react-query';
import cloneDeep from 'lodash/cloneDeep';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { configActions } from '../redux/slices/config';
import { workflowActions } from '../redux/slices/workflow';
import Workflow from '../workflow';
import { getBackofficeConfig } from './api';

const Backoffice = ({ config }) => {
  const dispatch = useDispatch();
  // get flowName from config/url/cookie
  const flowName = useFlowName(config);

  const searchParams = new URLSearchParams(window.location.search);
  const envBO = searchParams.get('envBO') || undefined;

  // flowName used to get the correct flow from BO
  const {
    data: backofficeConfig,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['backofficeConfig'],
    queryFn: () =>
      getBackofficeConfig(
        flowName,
        config.translation?.language,
        config.brand,
        config?.isPreviewOn,
        envBO
      ),
  });
  useEffect(() => {
    if (backofficeConfig) {
      dispatch(workflowActions.setBackofficeConfig(backofficeConfig));
    }
  }, [backofficeConfig, dispatch]);
  if (!backofficeConfig || isLoading || isError) return null;
  else {
    return <Backend config={config} backofficeConfig={backofficeConfig} />;
  }
};

const Backend = ({ config, backofficeConfig }) => {
  // get content / packages / prices from data manager (akamai) if not included in config object from ecommerce
  const configFromBackend = useBackend(config, backofficeConfig);

  const storeConfig = useSelector((state: any) => state.config);
  const configContent = useSelector((state: any) => state.config.content);
  const configPackages = useSelector((state: any) => state.config.packages);

  // preCheck logs about config object
  usePreCheck(configFromBackend);

  const dispatch = useDispatch();

  // check per i bundles (TO)
  useBundlesPrices();
  useShowFramePlusLensesPrice();

  // speedy RX
  useEnableSpeedyRX();
  // A/B Test packages
  const abTestFilter = useEnableRemoveLenses();

  useEffect(() => {
    if (configFromBackend?.lensesData?.content) {
      dispatch(
        configActions.setConfigContent(configFromBackend.lensesData.content)
      );
    }
    if (configFromBackend?.lensesData?.packages) {
      dispatch(
        configActions.setConfigPackages(configFromBackend.lensesData.packages)
      );
    }
    if (configFromBackend) {
      console.info('Received config: ', configFromBackend);
      if (abTestFilter) {
        configFromBackend.lensesData.packages =
          configFromBackend.lensesData.packages.filter((p) =>
            abTestFilter(p.lensPackage)
          );

        console.log(
          'RXC3 | A/B Test filtered pkgs: ',
          configFromBackend.lensesData.packages.filter((p) =>
            abTestFilter(p.lensPackage)
          )
        );
      }
      dispatch(configActions.setConfig(cloneDeep(configFromBackend)));
    }
  }, [configFromBackend, dispatch]);

  // LA

  const cookieLA = useCookieLensAdvisor();
  const [standAloneLA, setStandAloneLA] = useState(
    cookieLA?.laStandalone ?? false
  );

  const loadLAScript = (src) => {
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = src;
      script.type = 'application/javascript';
      script.crossOrigin = 'anonymous';
      script.defer = true;
      script.onload = () => resolve(script);
      script.onerror = () => reject(new Error(`Script load error for ${src}`));
      document.head.append(script);
    });
  };
  let tempLAModule = { ...config.lensAdvisorModule };
  const openLensAdvisor = () => {
    let laDiv = document.createElement('div');
    laDiv.id = 'laApp'; //put la selector
    document.body.appendChild(laDiv);

    let newConfig = {
      ...storeConfig,
      useGetReviewObjectLensAdvisor,
    };
    newConfig.lensesData = {
      packages: window.rxcBrain.config.lensesData.packages,
    };
    console.log('RXC - LA | Config: ', newConfig);
    if (newConfig) {
      if (cookieLA?.laContent !== null && cookieLA?.laContent !== undefined) {
        tempLAModule = {
          ...tempLAModule,
          content: cookieLA?.laContent,
        };
      }
      if (cookieLA?.laStyle !== null && cookieLA?.laStyle !== undefined) {
        tempLAModule = {
          ...tempLAModule,
          style: cookieLA?.laStyle,
        };
      }
      if (cookieLA?.laSurvey !== null && cookieLA?.laSurvey !== undefined) {
        tempLAModule = {
          ...tempLAModule,
          surveyData: {
            ...tempLAModule.surveyData,
            survey: cookieLA?.laSurvey,
          },
        };
      }
      if (cookieLA?.laRules !== null && cookieLA?.laRules !== undefined) {
        tempLAModule = {
          ...tempLAModule,
          surveyData: {
            ...tempLAModule.surveyData,
            rules: cookieLA?.laRules,
          },
        };
      }
      if (
        cookieLA?.laStandalone !== null &&
        cookieLA?.laStandalone !== undefined
      ) {
        tempLAModule = {
          ...tempLAModule,
          standAloneMode: cookieLA?.laStandalone,
        };
      }
      newConfig = {
        ...newConfig,
        lensAdvisorModule: tempLAModule,
      };

      var laApp = window?.LensAdvisor?.widget.new(newConfig);
      laApp.init();
    } else {
      if (cookieLA?.laContent !== null && cookieLA?.laContent !== undefined) {
        tempLAModule = {
          ...tempLAModule,
          content: cookieLA?.laContent,
        };
      }
      if (cookieLA?.laStyle !== null && cookieLA?.laStyle !== undefined) {
        tempLAModule = {
          ...tempLAModule,
          style: cookieLA?.laStyle,
        };
      }
      if (cookieLA?.laSurvey !== null && cookieLA?.laSurvey !== undefined) {
        tempLAModule = {
          ...tempLAModule,
          surveyData: {
            ...tempLAModule.surveyData,
            survey: cookieLA?.laSurvey,
          },
        };
      }
      if (cookieLA?.laRules !== null && cookieLA?.laRules !== undefined) {
        tempLAModule = {
          ...tempLAModule,
          surveyData: {
            ...tempLAModule.surveyData,
            rules: cookieLA?.laRules,
          },
        };
      }
      if (
        cookieLA?.laStandalone !== null &&
        cookieLA?.laStandalone !== undefined
      ) {
        tempLAModule = {
          ...tempLAModule,
          standAloneMode: cookieLA?.laStandalone,
        };
      }
      let Rconfig = {
        ...storeConfig,
        lensAdvisorModule: tempLAModule,
      };
      Rconfig.lensesData = {
        packages: storeConfig.lensesData.packages,
      };
      var laApp = window?.LensAdvisor?.widget.new(Rconfig);
      laApp.init();
    }
  };

  useEffect(() => {
    if (config?.lensAdvisorModule && configPackages?.length) {
      loadLAScript(
        `https://rxc.luxottica.com/rxc3/la/${config?.lensAdvisorModule?.laEnv?.toLowerCase()}/${
          config?.lensAdvisorModule?.laVersion
        }/build/lens_advisor.js`
      )
        .then(() => {
          const isStandAlone =
            cookieLA?.laStandalone || config?.lensAdvisorModule?.standAloneMode;
          if (
            isStandAlone === true ||
            config?.lensAdvisorModule?.laCartMode?.branded?.length > 0 ||
            config?.lensAdvisorModule?.laCartMode?.unbranded?.length > 0
          ) {
            setStandAloneLA(true);
            openLensAdvisor();
          }
        })
        .catch((error) => {
          console.error(error);
        });
    }
  }, [config?.lensAdvisorModule, configPackages]);

  if (
    configPackages.length > 0 &&
    (!abTestFilter ||
      configPackages.filter((cp) => abTestFilter(cp.lensPackage)).length) &&
    Object.keys(configContent).length > 0 &&
    window.rxcBrain.config.lensesData?.packages?.length > 0 &&
    window.rxcBrain?.config?.lensesData?.content &&
    Object.keys(window.rxcBrain.config.lensesData.content).length > 0
  )
    if (standAloneLA) {
      return null;
    } else {
      return <Workflow backofficeConfig={backofficeConfig} />;
    }
  else return null;
};

export default Backoffice;
